import type { ChapterQuestionFilterTypeEnum, PdfContentTypeEnum } from "@/enums/content";
import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import {
  ContentPdfReviewDisplayView,
  ContentPdfTaggingDisplayView,
  PdfQuestion,
  QuestionDetails,
  QuestionImageUploadsInput,
  QuestionImageUploadUrl,
  QuestionPdfExtractedData,
  QuestionPdfInfo,
  QuestionReviewDisplay,
  QuestionUrl,
  SimilarQuestionPair,
  SolutionUrl,
  SubmitQuestionPdfInputs,
} from "@/types/content";
import { ApiResponse, AzureUploadUrlWithUUID } from "@/types/global";
import { SelectorQuestion } from "@/types/questionSelector";
export default class QuestionApi extends ApiVerified {
  async getQuestionDetails(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<QuestionDetails>(
        `question/get-question-details`,
        { params: { questionId: questionId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionDisplayUrl(questionId: number, displayType: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<string>(`question/get-question-display-url`, {
        params: { questionId: questionId, displayType: displayType },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionHtmlImageUrl(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<QuestionUrl>(
        `question/get-question-html-image`,
        { params: { questionId: questionId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSolutionHtmlImageUrl(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SolutionUrl>(
        `question/get-solution-html-image`,
        { params: { questionId: questionId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionConceptImages(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<string>>(
        `question/get-question-concept-images`,
        { params: { questionId: questionId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionHintsHtml(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<string>>(
        `question/get-question-hints-html`,
        { params: { questionId: questionId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async fixQuestion(updatedQuestion: QuestionDetails): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `question/fix-question`,
        updatedQuestion,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // The tagging editor's add: upserts the picked mapping with the given active state;
  // an active mapping becomes the question's primary within its course
  // (POST question/{questionId}/content-tagging). Returns the mapping-row id.
  async addQuestionContentTagging(
    questionId: number,
    topicConceptId: number,
    isActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `question/${questionId}/content-tagging`,
        { TopicConceptId: topicConceptId, IsActive: isActive },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // The tagging editor's edit: re-points the existing mapping row to the picked
  // TopicConceptId and/or toggles its active state
  // (PUT question/{questionId}/content-tagging/{questionTopicConceptId}).
  async updateQuestionContentTagging(
    questionId: number,
    questionTopicConceptId: number,
    topicConceptId: number,
    isActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `question/${questionId}/content-tagging/${questionTopicConceptId}`,
        { TopicConceptId: topicConceptId, IsActive: isActive },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async fixMultipleQuestions(questionDetails: Array<QuestionDetails>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `question/fix-multiple-questions`,
        questionDetails,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async publishGenerateQuestionHtmlRequests(questionIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `question/publish-generate-question-html-requests`,
        { QuestionIds: questionIds },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionPdfUploadUrl(
    contentType: string,
    fileMd5Checksum: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrlWithUUID>(
        `question/get-question-pdf-upload-url`,
        { params: { contentType: contentType, fileMd5Checksum: fileMd5Checksum } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitQuestionPdf(inputs: SubmitQuestionPdfInputs): Promise<ApiResponse> {
    try {
      // AR-12.7: the endpoint returns the created PDF UUID (string), not a boolean.
      const response = await this.axiosInstance.post<string>(
        `question/submit-question-pdf`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPdfQuestionsForCategoryTagging(
    pdfUUID: string,
    pdfContentType: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ContentPdfTaggingDisplayView>(
        `question/get-pdf-questions-for-category-tagging`,
        { params: { pdfUUID: pdfUUID, pdfContentType: pdfContentType } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionImageUploadUrls(
    questionImageUploadsInput: QuestionImageUploadsInput,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<QuestionImageUploadUrl>>(
        `question/get-question-image-upload-urls`,
        questionImageUploadsInput,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitCategoryTaggedQuestions(questions: Array<PdfQuestion>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `question/submit-category-tagged-questions`,
        questions,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Consolidated review read (AR-0.4/AR-12.1): serves both question and chapter PDFs,
  // discriminated by the required pdfContentType param.
  async getQuestionPdfQuestionsForReview(
    pdfUUID: string,
    pdfContentType: PdfContentTypeEnum,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ContentPdfReviewDisplayView>(
        `question/get-pdf-questions-for-review`,
        { params: { pdfUUID: pdfUUID, pdfContentType: pdfContentType } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveReviewedQuestions(questions: Array<QuestionReviewDisplay>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `question/save-reviewed-questions`,
        questions,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async loadAllQuestionPdfs(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<QuestionPdfInfo>>(
        `question/get-all-question-pdfs`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async resubmitPendingQuestionPdf(pdfUUID: string | null): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `question/resubmit-pending-question-pdf`,
        { PdfUUID: pdfUUID },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getChapterQuestionIdsByDiagram(subjectChapterId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<number>>(
        `question/get-chapter-question-ids-by-diagram`,
        { params: { subjectChapterId: subjectChapterId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Content-v2 consolidated read (AR-12.2, plan cv2a-3-3) — replaces the three by-status /
  // by-question-type / by-error-type methods via a `filterType` discriminator
  // (get-chapter-question-ids-by-diagram stays separate). Returns list[int].
  async getChapterQuestionIds(
    subjectChapterId: number,
    filterType: ChapterQuestionFilterTypeEnum,
    filterValueId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<number>>(
        `question/chapter-question-ids`,
        {
          params: {
            subjectChapterId: subjectChapterId,
            filterType: filterType,
            filterValueId: filterValueId,
            onlyActive: onlyActive,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationUploadedQuestions(
    affiliationId: number,
    subjectChapterId: number | null,
    questionTypeId: number | null,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SelectorQuestion>>(
        `question/get-affiliation-uploaded-questions`,
        {
          params: {
            affiliationId: affiliationId,
            subjectChapterId: subjectChapterId,
            questionTypeId: questionTypeId,
            onlyActive: onlyActive,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // AR-12.3: aligned to the doc twin — `get-ai-marked-similar-questions-for-question`
  // takes `questionId` (the frontend previously hit a non-existent chapter-scoped URL).
  async getAiMarkedSimilarQuestionsForQuestion(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SimilarQuestionPair>>(
        `question/get-ai-marked-similar-questions-for-question`,
        { params: { questionId: questionId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAiMarkedSimilarQuestionsForSubjectChapter(
    subjectChapterId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SimilarQuestionPair>>(
        `question/get-ai-marked-similar-questions-for-subject-chapter`,
        { params: { subjectChapterId: subjectChapterId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateReviewedSimilarQuestions(
    questionPairs: Array<SimilarQuestionPair>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `question/update-reviewed-similar-questions`,
        questionPairs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionPdfExtractedData(pdfUUID: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<QuestionPdfExtractedData>(
        `question/get-question-pdf-extracted-data`,
        { params: { pdfUUID: pdfUUID } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
  async addMissingRawQuestion(
    pdfUUID: string,
    rawQuestionText: string,
    serialNumber: number,
    pageNumber: number,
    pdfPageNumber: number | null = null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`question/add-missing-raw-question`, {
        PdfUUID: pdfUUID,
        RawQuestionText: rawQuestionText,
        SerialNumber: serialNumber,
        PageNumber: pageNumber,
        PdfPageNumber: pdfPageNumber,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async confirmPdfExtraction(pdfUUID: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`question/confirm-pdf-extraction`, {
        PdfUUID: pdfUUID,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
